import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DeletionCommitResult, DeletionImpact } from '@vivport/backend/types';
import { useContentDeletionPreview, useContentDeletionCommit } from '@/hooks/useContentQueries';
import { DeletionImpactTree } from './DeletionImpactTree';
import { BLOCK_REASON_COPY, nodeTitle, nodeTypeLabel } from './deletion-labels';
import {
  getAxiosErrorCode,
  getAxiosErrorDisplayMessage,
  getAxiosErrorStatus,
} from '@/utils/error-utils';
import { Notice } from '@/components/ui/notice';

const MAX_CASCADE_DEPTH = 10;

function Figure({
  label,
  value,
  detail,
  emphasis = false,
}: {
  label: string;
  value: number;
  detail?: string;
  emphasis?: boolean;
}) {
  return (
    <div className="min-w-[7rem] flex-1">
      <div className={cn('text-2xl font-medium tabular-nums', emphasis && 'text-destructive')}>
        {value}
      </div>
      <div className={cn('text-xs font-medium', emphasis ? 'text-destructive' : 'text-foreground')}>
        {label}
      </div>
      {detail ? <p className="mt-1 text-xs text-muted-foreground">{detail}</p> : null}
    </div>
  );
}

interface DeleteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedItems: number[];
  /**
   * The delete episode is over and the selection may be cleared. Fires immediately on a
   * clean commit, and on dialog close after a partial one — by then the member has read
   * the report and the leftovers are no longer worth holding.
   */
  onDeleted: (result: DeletionCommitResult) => void;
  /**
   * A commit landed but left something behind. Refresh lists; do NOT clear the
   * selection — the items that did not go stay selected so the member can act on them
   * while the report is still on screen.
   */
  onPartiallyDeleted: (result: DeletionCommitResult) => void;
}

export function Delete({
  open,
  onOpenChange,
  selectedItems,
  onDeleted,
  onPartiallyDeleted,
}: DeleteProps) {
  const [cascadeAll, setCascadeAll] = useState(false);
  const [optionalIncluded, setOptionalIncluded] = useState<Set<number>>(new Set());
  const [cascadeDepth, setCascadeDepth] = useState(0);
  const [confirmText, setConfirmText] = useState('');
  const [staleNotice, setStaleNotice] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [partialResult, setPartialResult] = useState<DeletionCommitResult | null>(null);
  const [committedImpact, setCommittedImpact] = useState<DeletionImpact | null>(null);

  const optionalArray = useMemo(
    () => [...optionalIncluded].sort((a, b) => a - b),
    [optionalIncluded]
  );

  const {
    data: impact,
    isFetching,
    refetch,
  } = useContentDeletionPreview(
    { roots: selectedItems, optionalIncluded: optionalArray },
    { enabled: open }
  );

  const commit = useContentDeletionCommit();

  // Turning the master toggle on pulls in every OPTIONAL node the preview offers.
  // Including them re-runs the preview, which can offer a further level down, so this
  // repeats until a pass adds nothing new (the fixed point) or MAX_CASCADE_DEPTH passes
  // have run. The bound is a safety net against a pathological graph, not an expected
  // limit. The isFetching guard keeps the pass from reading a stale placeholder impact
  // and counting the same level twice.
  useEffect(() => {
    if (!cascadeAll || !impact || isFetching) return;
    if (cascadeDepth >= MAX_CASCADE_DEPTH) return;
    const offered = impact.nodes
      .filter((node) => node.inclusion === 'OPTIONAL')
      .map((node) => node.contentId);
    const missing = offered.filter((id) => !optionalIncluded.has(id));
    if (missing.length === 0) return;
    setOptionalIncluded((prev) => new Set([...prev, ...missing]));
    setCascadeDepth((depth) => depth + 1);
  }, [cascadeAll, impact, isFetching, optionalIncluded, cascadeDepth]);

  useEffect(() => {
    if (open) return;
    setCascadeAll(false);
    setOptionalIncluded(new Set());
    setCascadeDepth(0);
    setConfirmText('');
    setStaleNotice(false);
    setErrorMessage(null);
    setPartialResult(null);
    setCommittedImpact(null);
  }, [open]);

  // A post-commit impact is a RECORD OF WHAT WAS ATTEMPTED, not a live view. The preview
  // query runs with staleTime 0 and refetches on window focus, so without this a single
  // alt-tab would re-render the partial report against the post-delete graph. It is also
  // what keeps the body off the spinner if the selection ever empties, since the query
  // disables itself at roots: []. The body reads shownImpact; the ACTIONS below stay on
  // the live impact, because a frozen plan is not something you can commit against.
  const shownImpact = committedImpact ?? impact;

  // After a commit lands the dialog stops being a planner: the plan it describes has
  // already run and its planHash is spent, so re-confirming would 409 by construction.
  const isReporting = partialResult !== null;

  const cascadeSettling = cascadeAll && cascadeDepth > 0 && isFetching;
  const summary = shownImpact?.summary;
  // selectedCount names what the member picked in the list; goingCount names what will
  // actually be removed, including every OPTIONAL node they ticked (inclusion stays
  // OPTIONAL after opt-in, so selectedCount + forcedCount silently omits them). A header
  // reading 1 above a button reading 4 is these two facts, not a bug.
  const totalGoing = summary?.goingCount ?? 0;
  const blockedNodes = shownImpact?.nodes.filter((node) => node.blocked.length > 0) ?? [];

  const shownNodeById = useMemo(
    () => new Map((shownImpact?.nodes ?? []).map((node) => [node.contentId, node])),
    [shownImpact]
  );
  // A skipped or failed id is usually a node in the frozen impact, but not always: the
  // impact is head-only and can be truncated, and a document subtree removes rows the
  // preview never listed. The bare id beats an unattributed reason.
  const describeEntry = (contentId: number): string => {
    const node = shownNodeById.get(contentId);
    return node ? `${nodeTypeLabel(node)} ${nodeTitle(node)}` : `Item ${contentId}`;
  };

  // Typing the word is friction, so it is spent only where a mistake is expensive: a
  // large batch, anything that will be left unfiled, or a forced subtree the member did
  // not pick item by item. Deleting one photo should not require a ceremony.
  const requiresTypedConfirm =
    summary !== undefined &&
    (totalGoing > 5 || summary.orphanCount > 0 || summary.forcedCount > 0);

  const confirmDisabled =
    !impact ||
    isReporting ||
    isFetching ||
    commit.isPending ||
    totalGoing === 0 ||
    (requiresTypedConfirm && confirmText !== 'Delete');

  const handleToggleCascade = (next: boolean) => {
    setCascadeAll(next);
    setCascadeDepth(0);
    if (!next) {
      setOptionalIncluded(new Set());
    }
  };

  // Stable so the memoised tree rows are not re-rendered by every parent render.
  const handleTreeToggle = useCallback((contentId: number, next: boolean) => {
    setOptionalIncluded((prev) => {
      const updated = new Set(prev);
      if (next) updated.add(contentId);
      else updated.delete(contentId);
      return updated;
    });
    // Unchecking means the member has stopped cascading and started curating. Leaving
    // cascadeAll on would let the settle effect re-add what they just removed. The flag
    // is cleared directly rather than through handleToggleCascade, which would also
    // discard the rest of their selection.
    if (!next) setCascadeAll(false);
  }, []);

  const handleConfirm = () => {
    if (!impact || confirmDisabled) return;
    setStaleNotice(false);
    setErrorMessage(null);
    setPartialResult(null);
    commit.mutate(
      { roots: selectedItems, optionalIncluded: optionalArray, planHash: impact.planHash },
      {
        onSuccess: (result) => {
          if (result.failed.length === 0 && result.skipped.length === 0) {
            onDeleted(result);
            onOpenChange(false);
            return;
          }
          // Freeze what was on screen: from here the dialog reports, and the live
          // preview would drift to the post-delete graph.
          if (impact) setCommittedImpact(impact);
          setPartialResult(result);
          onPartiallyDeleted(result);
          setConfirmText('');
        },
        onError: (error) => {
          if (
            getAxiosErrorStatus(error) === 409 &&
            getAxiosErrorCode(error) === 'DELETION_PLAN_STALE'
          ) {
            setStaleNotice(true);
            setConfirmText('');
            void refetch();
            return;
          }
          setErrorMessage(
            getAxiosErrorDisplayMessage(error, 'The delete could not be completed.')
          );
        },
      }
    );
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (commit.isPending) return;
    // Dismissing a partial report ends the episode: the member has seen what was left
    // behind, so the caller may now clear.
    if (!nextOpen && partialResult) onDeleted(partialResult);
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            {/* Roots only — what the member picked in the list. The confirm button below
                reads goingCount, which also includes everything they ticked, so this
                number is deliberately smaller. See the totalGoing comment above. */}
            Delete {summary?.selectedCount ?? selectedItems.length} item
            {(summary?.selectedCount ?? selectedItems.length) === 1 ? '' : 's'}
          </DialogTitle>
          <DialogDescription>
            This removes the content permanently. There is no undo and no recycle bin.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {!shownImpact ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Working out what this would remove…
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-4 rounded-md border p-4">
                <Figure label="Deleting" value={totalGoing} />
                {shownImpact.summary.goingOptionalCount > 0 && (
                  <Figure
                    label="Attached items you included"
                    value={shownImpact.summary.goingOptionalCount}
                    detail="Ticked by you in the list below. Untick any to leave it in place."
                  />
                )}
                {shownImpact.summary.forcedCount > 0 && (
                  <Figure
                    label="Also removed"
                    value={shownImpact.summary.forcedCount}
                    detail="Document sections and message replies cannot be separated from what they belong to, so they go with it."
                  />
                )}
                {shownImpact.summary.orphanCount > 0 && (
                  <Figure
                    label="Orphaned"
                    value={shownImpact.summary.orphanCount}
                    detail="Stays in the hub but loses its last parent, so it will no longer be filed anywhere."
                    emphasis
                  />
                )}
                {shownImpact.summary.blockedCount > 0 && (
                  <Figure
                    label="Blocked"
                    value={shownImpact.summary.blockedCount}
                    detail="Listed below with the reason."
                    emphasis
                  />
                )}
                {shownImpact.summary.revisionRowCount >
                  shownImpact.summary.selectedCount +
                    shownImpact.summary.forcedCount +
                    shownImpact.summary.optionalCount && (
                  <Figure
                    label="Earlier versions"
                    value={shownImpact.summary.revisionRowCount}
                    detail="Rows removed including every earlier version."
                  />
                )}
              </div>

              {shownImpact.truncated && (
                <p className="text-xs text-muted-foreground">
                  The figures above are exact. The list below shows the first{' '}
                  {shownImpact.nodes.length} of {shownImpact.nodes.length + shownImpact.truncatedCount} items.
                </p>
              )}

              <div className="space-y-1">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="cascade-all"
                    checked={cascadeAll}
                    onCheckedChange={(checked) => handleToggleCascade(checked === true)}
                    disabled={cascadeSettling || commit.isPending || isReporting}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="cascade-all" className="font-medium">
                      Also delete everything attached below these items
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Off by default: only what you selected and what cannot be separated from it.
                    </p>
                    {shownImpact.truncated && (
                      <p className="text-xs text-muted-foreground">
                        The list is too large to show in full, so items cannot be picked
                        individually. This switch applies to everything in scope.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <DeletionImpactTree
                impact={shownImpact}
                optionalIncluded={optionalIncluded}
                onToggle={handleTreeToggle}
                disabled={commit.isPending || cascadeSettling || isReporting}
              />

              {blockedNodes.length > 0 && (
                <div className="space-y-2 rounded-md border p-3">
                  <p className="text-sm font-medium">These will not be deleted.</p>
                  <ul className="space-y-2">
                    {blockedNodes.map((node) => (
                      <li key={node.contentId} className="text-sm">
                        <span className="font-medium">{nodeTypeLabel(node)}</span>{' '}
                        <span>{nodeTitle(node)}</span>
                        <p className="text-xs text-muted-foreground">
                          {BLOCK_REASON_COPY[node.blocked[0]]}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {staleNotice && (
                <Notice tone="destructive" className="p-3">
                  <p className="text-sm font-medium text-destructive">
                    This content changed while you were reviewing it.
                  </p>
                  <p className="text-sm">
                    Nothing was deleted. The figures above have been refreshed — check them again
                    before confirming.
                  </p>
                </Notice>
              )}

              {errorMessage && (
                <Notice tone="destructive" className="p-3">
                  <p className="text-sm text-destructive">{errorMessage}</p>
                </Notice>
              )}

              {partialResult && (
                <Notice tone="destructive" className="space-y-2 p-3">
                  <p className="text-sm font-medium">
                    {partialResult.deletedNodeCount} item
                    {partialResult.deletedNodeCount === 1 ? '' : 's'} deleted. Some were left
                    behind.
                  </p>
                  <ul className="space-y-1 text-sm">
                    {partialResult.skipped.map((entry) => (
                      <li key={`skipped-${entry.contentId}`}>
                        <span className="font-medium">{describeEntry(entry.contentId)}</span>
                        {' — '}
                        {BLOCK_REASON_COPY[entry.reason]}
                      </li>
                    ))}
                    {partialResult.failed.map((entry) => (
                      <li key={`failed-${entry.contentId}`}>
                        <span className="font-medium">{describeEntry(entry.contentId)}</span>
                        {' — '}
                        {entry.errorMessage}
                      </li>
                    ))}
                  </ul>
                </Notice>
              )}

              {requiresTypedConfirm && (
                <div className="space-y-2">
                  <Label htmlFor="confirm-delete">
                    Type <strong>Delete</strong> to confirm:
                  </Label>
                  <Input
                    id="confirm-delete"
                    placeholder="Delete"
                    value={confirmText}
                    onChange={(event) => setConfirmText(event.target.value)}
                    disabled={commit.isPending}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                  />
                </div>
              )}
            </>
          )}
        </DialogBody>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={commit.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={confirmDisabled}
            className="min-w-[140px]"
          >
            {commit.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Deleting…
              </>
            ) : (
              `Delete ${totalGoing} item${totalGoing === 1 ? '' : 's'}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
